import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, FileTextIcon, BriefcaseIcon, BuildingIcon, UserIcon, SaveIcon, SendIcon } from 'lucide-react';
import { toast } from 'sonner';
// Step components
import Step1Basics from '../../components/scholarships/ScholarshipForm/Step1Basics';
import Step2ProgramsUniversities from '../../components/scholarships/ScholarshipForm/Step2ProgramsUniversities';
import Step3ApplicationForm from '../../components/scholarships/ScholarshipForm/Step3ApplicationForm';
import Step4Review from '../../components/scholarships/ScholarshipForm/Step4Review';
const CreateScholarship: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Scholarship Basics
    scholarshipTitle: '',
    seatsAvailable: '',
    durationYears: '',
    coverageTypes: [],
    // Step 2: Programs & Universities
    programName: '',
    preferredUniversities: [],
    // Step 3: Application Form
    eligibilityPreset: 'flexible',
    formFields: [],
    // Step 4: Review & Submit
    status: 'Draft'
  });
  const steps = [{
    id: 1,
    name: 'Scholarship Basics',
    icon: <FileTextIcon className="w-5 h-5" data-id="element-2392" />
  }, {
    id: 2,
    name: 'Programs & Universities',
    icon: <BuildingIcon className="w-5 h-5" data-id="element-2393" />
  }, {
    id: 3,
    name: 'Application Form',
    icon: <UserIcon className="w-5 h-5" data-id="element-2394" />
  }, {
    id: 4,
    name: 'Review & Submit',
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-2395" />
  }];
  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  const handleUpdateFormData = (stepData: Partial<typeof formData>) => {
    setFormData({
      ...formData,
      ...stepData
    });
  };
  const handleSubmit = (status: 'Draft' | 'Pending MoHESR' | 'Active') => {
    // In a real app, you would submit the form data to your backend here
    console.log('Submitting scholarship with status:', status);
    console.log('Form data:', formData);
    if (status === 'Pending MoHESR') {
      toast.success('Scholarship submitted to MoHESR for approval', {
        duration: 3000,
        icon: <SendIcon className="w-5 h-5" data-id="element-2396" />
      });
    } else if (status === 'Draft') {
      toast.success('Scholarship saved as draft', {
        duration: 3000,
        icon: <SaveIcon className="w-5 h-5" data-id="element-2397" />
      });
    }
    // Navigate back to scholarships page
    navigate('/employer/scholarships');
  };
  const handleSaveDraft = () => {
    console.log('Saving draft:', formData);
    // Here you would save the form data to your backend
    toast.success('Draft saved successfully!', {
      duration: 3000,
      icon: <SaveIcon className="w-5 h-5" data-id="element-2398" />
    });
  };
  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Basics formData={formData} updateFormData={handleUpdateFormData} data-id="element-2399" />;
      case 2:
        return <Step2ProgramsUniversities formData={formData} updateFormData={handleUpdateFormData} data-id="element-2400" />;
      case 3:
        return <Step3ApplicationForm formData={formData} updateFormData={handleUpdateFormData} data-id="element-2401" />;
      case 4:
        return <Step4Review formData={formData} updateFormData={handleUpdateFormData} onSubmit={handleSubmit} data-id="element-2402" />;
      default:
        return null;
    }
  };
  return <Layout title="Create Partnership Scholarship" persona="Company/Partner" data-id="element-2403">
      <div className="max-w-5xl mx-auto" data-id="element-2404">
        {/* Step progress indicator */}
        <nav aria-label="Progress" className="mb-10" data-id="element-2405">
          <ol className="flex items-center justify-between w-full" data-id="element-2406">
            {steps.map((step, index) => <li key={step.id} className="relative flex flex-col items-center" data-id="element-2407">
                {/* Connecting line before */}
                {index > 0 && <div className="absolute top-4 right-full w-full h-0.5" aria-hidden="true" data-id="element-2408">
                    <div className={`h-0.5 ${currentStep > index ? 'bg-primary-500' : 'bg-gray-200'}`} style={{
                width: 'calc(100% - 16px)',
                marginLeft: '8px'
              }} data-id="element-2409" />
                  </div>}
                {/* Step indicator */}
                <button onClick={() => step.id < currentStep && setCurrentStep(step.id)} className={`relative flex h-8 w-8 items-center justify-center rounded-full z-10 
                    ${currentStep > step.id ? 'bg-primary-500 hover:bg-primary-600' : currentStep === step.id ? 'bg-primary-50 border-2 border-primary-500' : 'bg-white border-2 border-gray-300'} 
                    ${step.id < currentStep ? 'cursor-pointer' : 'cursor-default'}`} disabled={step.id >= currentStep} aria-current={currentStep === step.id ? 'step' : undefined} data-id="element-2410">
                  {currentStep > step.id ? <CheckCircleIcon className="h-5 w-5 text-white" aria-hidden="true" data-id="element-2411" /> : <span className={currentStep === step.id ? 'text-primary-500' : 'text-gray-500'} data-id="element-2412">
                      {step.icon}
                    </span>}
                  <span className="sr-only" data-id="element-2413">{step.name}</span>
                </button>
                {/* Step label */}
                <div className="text-center mt-2" data-id="element-2414">
                  <span className={`text-xs font-medium ${currentStep === step.id ? 'text-primary-500' : currentStep > step.id ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-2415">
                    {step.name}
                  </span>
                </div>
              </li>)}
          </ol>
        </nav>
        {/* Form content */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-2416">
          {renderStep()}
        </div>
        {/* Navigation buttons */}
        <div className="flex justify-between mt-6" data-id="element-2417">
          <button type="button" onClick={handlePrevious} disabled={currentStep === 1} className={`px-4 py-2 rounded-md flex items-center text-sm font-medium ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-2418">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2419" />
            Previous
          </button>
          <div className="flex space-x-3" data-id="element-2420">
            {/* Save Draft button available on all steps */}
            <button type="button" onClick={handleSaveDraft} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-gray-50" data-id="element-2421">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-2422" />
              Save Draft
            </button>
            {currentStep === steps.length /* Review step buttons */ ? <button type="button" onClick={() => handleSubmit('Pending MoHESR')} className="bg-primary-500 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-primary-600" data-id="element-2423">
                <SendIcon className="w-4 h-4 mr-2" data-id="element-2424" />
                Submit to MoHESR
              </button> : <button type="button" onClick={handleNext} className="bg-primary-500 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-primary-600" data-id="element-2425">
                Next
                <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2426" />
              </button>}
          </div>
        </div>
      </div>
    </Layout>;
};
export default CreateScholarship;